import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  CheckCircle2,
  Circle,
  Lock,
  Sparkles,
  Trophy,
  Calendar,
  Flame,
  TrendingUp,
  Compass,
  RefreshCcw,
  Star,
  Share2,
} from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import WeKitLogo from "@/components/WeKitLogo";
import VisionBoardCta from "@/components/VisionBoardCta";
import {
  buildGrowthSeries,
  buildSchedule,
  computeReadiness,
  daysBetween,
  deriveBadges,
  fetchMyJourney,
  fetchReadinessSignals,
  formatMonth,
  type JourneyPayload,
  type MilestoneKind,
  type ReadinessBreakdown,
  type ScheduledStep,
} from "@/lib/journey";
import { TooltipProvider, Tooltip as UiTooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { Info } from "lucide-react";

import { analytics } from "@/lib/analytics";
import RoadmapPanel from "@/components/roadmap/RoadmapPanel";
import VisionJourneyMap from "@/components/journey/VisionJourneyMap";

const KIND_ICON: Record<MilestoneKind, React.ComponentType<{ className?: string }>> = {
  initial_assessment: Compass,
  reflection: Sparkles,
  quarterly_refresh: RefreshCcw,
  mid_year_refresh: RefreshCcw,
  annual_refresh: Star,
};

const STATUS_STYLES: Record<
  ScheduledStep["status"],
  { dot: string; ring: string; pill: string; pillLabel: string }
> = {
  completed: {
    dot: "bg-primary text-primary-foreground",
    ring: "ring-primary/30",
    pill: "bg-primary/15 text-primary border-primary/30",
    pillLabel: "Completed",
  },
  current: {
    dot: "bg-accent text-accent-foreground animate-pulse",
    ring: "ring-accent/40",
    pill: "bg-accent/15 text-accent-foreground border-accent/40",
    pillLabel: "Ready now",
  },
  upcoming: {
    dot: "bg-muted text-muted-foreground",
    ring: "ring-border",
    pill: "bg-muted text-muted-foreground border-border",
    pillLabel: "Upcoming",
  },
  locked: {
    dot: "bg-muted text-muted-foreground",
    ring: "ring-border",
    pill: "bg-muted text-muted-foreground border-border",
    pillLabel: "Locked",
  },
};

function nextStepLink(step: ScheduledStep): string {
  if (step.kind === "initial_assessment") return "/start";
  if (step.kind === "reflection") return `/reflect/${step.monthIndex}`;
  return "/journey";
}

export default function Journey() {
  const { user } = useAuth();
  const [data, setData] = useState<JourneyPayload | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    analytics.journeyView();
    let active = true;
    fetchMyJourney().then(async (p) => {
      if (!active) return;
      setData(p);
      setLoading(false);
      // Auto-lock profile once initial assessment is completed
      const hasInitial = (p.milestones ?? []).some((m) => m.kind === "initial_assessment");
      if (hasInitial && user?.id) {
        const { supabase } = await import("@/integrations/supabase/client");
        await supabase
          .from("profiles")
          .update({ locked_at: new Date().toISOString() })
          .eq("id", user.id)
          .is("locked_at", null);
      }
    });
    return () => {
      active = false;
    };
  }, [user?.id]);

  const steps = useMemo(
    () => buildSchedule(data?.membership ?? null, data?.milestones ?? []),
    [data],
  );
  const badges = useMemo(() => deriveBadges(steps), [steps]);
  const series = useMemo(() => buildGrowthSeries(steps), [steps]);

  const [readiness, setReadiness] = useState<ReadinessBreakdown | null>(null);
  useEffect(() => {
    let active = true;
    fetchReadinessSignals(steps, badges).then((sig) => {
      if (!active) return;
      setReadiness(computeReadiness(sig));
    });
    return () => {
      active = false;
    };
  }, [steps, badges]);

  const now = new Date();

  const started = data?.membership ? new Date(data.membership.started_at) : now;
  const expires = data?.membership ? new Date(data.membership.expires_at) : null;
  const daysIn = daysBetween(started, now);
  const daysLeft = expires ? daysBetween(now, expires) : 365;
  const totalDays = 365;
  const progressPct = Math.min(100, Math.round((daysIn / totalDays) * 100));
  const completedCount = steps.filter((s) => s.status === "completed").length;
  const earnedBadges = badges.filter((b) => b.earned).length;
  const nextStep = steps.find((s) => s.status === "current") ?? steps.find((s) => s.status === "upcoming");

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-border/60 bg-background/85 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button asChild variant="ghost" size="sm" className="gap-1.5 -ml-2">
              <Link to="/dashboard">
                <ArrowLeft className="h-4 w-4" /> Dashboard
              </Link>
            </Button>
            <div className="hidden sm:block h-5 w-px bg-border" />
            <Link to="/" className="hidden sm:inline-flex">
              <WeKitLogo size="sm" />
            </Link>
          </div>
          <div className="flex items-center gap-2">
            <Button asChild size="sm" variant="outline" className="gap-1.5">
              <Link to="/settings">Settings</Link>
            </Button>
          <Button asChild size="sm" className="gap-1.5">
            <Link to="/wrapped">
              <Share2 className="h-3.5 w-3.5" /> Year in Review
            </Link>
          </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Your map — dreams + action steps from the Vision Board. */}
        <div className="mb-12">
          <VisionJourneyMap />
        </div>

        {/* Career DNA™ journey roadmap — the doing part, step by step. */}
        <div className="mb-10">
          <RoadmapPanel kind="journey" />
        </div>

        {/* Hero */}
        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-10"
        >
          <Badge variant="outline" className="mb-4 gap-1.5">
            <Sparkles className="h-3 w-3 text-primary" /> WCC360 Membership · 365-day journey
          </Badge>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl font-bold leading-[1.05] tracking-tight text-foreground">
            My Growth <span className="gradient-text">Journey</span>
          </h1>
          <p className="mt-4 text-muted-foreground text-base sm:text-lg max-w-2xl">
            Career development is not a one-time decision. It's a lifelong journey —
            and WCC360 grows with you.
          </p>
        </motion.section>

        {/* Progress ring + stats */}
        <section className="mb-10 grid gap-4 md:grid-cols-[260px_1fr]">
          <ProgressCard
            progressPct={progressPct}
            daysIn={daysIn}
            daysLeft={daysLeft}
            startedLabel={formatMonth(started)}
            expiresLabel={expires ? formatMonth(expires) : "—"}
            loading={loading}
          />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatTile icon={CheckCircle2} label="Milestones" value={completedCount} total={steps.length} />
            <StatTile icon={Trophy} label="Badges" value={earnedBadges} total={badges.length} />
            <StatTile icon={Flame} label="Reflection streak" value={completedCount > 0 ? Math.min(completedCount, 12) : 0} suffix="mo" />
            <ReadinessTile readiness={readiness} />
          </div>
        </section>

        {/* Next-up CTA */}
        {nextStep && (
          <Card className="mb-10 border-primary/30 bg-gradient-to-br from-primary/10 via-card to-card">
            <CardContent className="p-5 sm:p-6 flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 rounded-2xl bg-primary/15 text-primary flex items-center justify-center shrink-0">
                  {(() => {
                    const Icon = KIND_ICON[nextStep.kind];
                    return <Icon className="h-5 w-5" />;
                  })()}
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                    {nextStep.status === "current" ? "Ready for you" : "Next up"} · {formatMonth(nextStep.scheduledFor)}
                  </p>
                  <h3 className="font-display text-xl font-semibold text-foreground mt-1">
                    {nextStep.title}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1 max-w-xl">{nextStep.blurb}</p>
                </div>
              </div>
              <Button asChild className="shrink-0">
                <Link
                  to={nextStepLink(nextStep)}
                  onClick={() => analytics.journeyNextStepClick({ kind: nextStep.kind, month_index: nextStep.monthIndex, status: nextStep.status })}
                >
                  {nextStep.status === "current" ? "Start now" : "Preview"}
                </Link>
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Timeline + Growth (left) · Achievements (right) */}
        <div className="grid gap-8 lg:grid-cols-[1fr_320px] mb-12">
          <div className="space-y-12 min-w-0">
            {/* Timeline */}
            <section>
              <div className="flex items-end justify-between mb-5">
                <div>
                  <h2 className="font-display text-2xl font-bold text-foreground">12-month timeline</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Initial assessment, monthly reflections, and two future refreshes.
                  </p>
                </div>
              </div>

              <ol className="relative border-l-2 border-dashed border-border ml-4">
                {steps.map((step, idx) => (
                  <TimelineRow key={`${step.kind}-${step.monthIndex}`} step={step} index={idx} />
                ))}
              </ol>
            </section>

            {/* Growth chart */}
            <section>
              <Card className="border-border/50 bg-card/50 backdrop-blur">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-start justify-between flex-wrap gap-2">
                    <div>
                      <h2 className="font-display text-xl font-semibold text-foreground flex items-center gap-2">
                        <TrendingUp className="h-4 w-4 text-primary" /> Growth signals
                      </h2>
                      <p className="text-xs text-muted-foreground mt-1">
                        Clarity, future skills, and confidence evolving month over month.
                      </p>
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      Solid points = real completions · Faded = preview projection
                    </p>
                  </div>
                  <div className="h-64 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={series} margin={{ top: 10, right: 8, left: -12, bottom: 0 }}>
                        <defs>
                          <linearGradient id="jClarity" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.45} />
                            <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="jSkills" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="hsl(var(--accent))" stopOpacity={0.45} />
                            <stop offset="100%" stopColor="hsl(var(--accent))" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="jConf" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.4} />
                            <stop offset="100%" stopColor="#f59e0b" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                        <XAxis dataKey="month" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} />
                        <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} width={28} domain={[0, 100]} />
                        <Tooltip contentStyle={{ background: "hsl(var(--background))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                        <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" iconSize={8} />
                        <Area type="monotone" dataKey="Clarity" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#jClarity)" />
                        <Area type="monotone" dataKey="Skills" stroke="hsl(var(--accent))" strokeWidth={2} fill="url(#jSkills)" />
                        <Area type="monotone" dataKey="Confidence" stroke="#f59e0b" strokeWidth={2} fill="url(#jConf)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </section>
          </div>

          {/* Achievements — right panel */}
          <aside className="lg:sticky lg:top-20 lg:self-start space-y-4">
            <VisionBoardCta
              context="journey"
              variant="compact"
              highlight={nextStep ? nextStep.title : null}
            />

            <Card className="border-border/50 bg-card/50 backdrop-blur">
              <CardContent className="p-5 space-y-4">
                <div className="flex items-end justify-between gap-2">
                  <div>
                    <h2 className="font-display text-lg font-bold text-foreground">Achievements</h2>
                    <p className="text-xs text-muted-foreground mt-1">
                      Earn badges as your journey unfolds.
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground shrink-0">
                    <span className="font-display font-semibold text-foreground">{earnedBadges}</span> of {badges.length}
                  </p>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-1 gap-2.5">
                  {badges.map((b) => (
                    <div
                      key={b.key}
                      className={`rounded-2xl border p-3 flex items-center gap-3 transition ${
                        b.earned
                          ? "border-primary/30 bg-primary/5"
                          : "border-border bg-card/40 opacity-70"
                      }`}
                    >
                      <div
                        className={`h-10 w-10 rounded-2xl flex items-center justify-center text-xl shrink-0 ${
                          b.earned ? "bg-primary/15" : "bg-muted grayscale"
                        }`}
                      >
                        {b.emoji}
                      </div>
                      <div className="min-w-0">
                        <p className="font-display text-sm font-semibold text-foreground truncate">{b.label}</p>
                        <p className="text-[11px] text-muted-foreground truncate">{b.blurb}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </aside>
        </div>


        {/* Closing quote */}
        <section className="text-center py-10 border-t border-border">
          <p className="font-display text-xl sm:text-2xl text-foreground italic leading-snug max-w-2xl mx-auto">
            "Career development is not a one-time decision. It's a lifelong journey.
            WCC360 grows with you."
          </p>
          <p className="text-xs text-muted-foreground mt-4 inline-flex items-center gap-1.5">
            <Sparkles className="h-3 w-3 text-primary" /> Rooting You In Your Purpose
          </p>
        </section>
      </main>
    </div>
  );
}

// --- Sub-components --------------------------------------------------------

function ProgressCard({
  progressPct,
  daysIn,
  daysLeft,
  startedLabel,
  expiresLabel,
  loading,
}: {
  progressPct: number;
  daysIn: number;
  daysLeft: number;
  startedLabel: string;
  expiresLabel: string;
  loading: boolean;
}) {
  const R = 52;
  const C = 2 * Math.PI * R;
  const offset = C - (progressPct / 100) * C;
  return (
    <Card className="border-border/50 bg-card/50 backdrop-blur">
      <CardContent className="p-5 flex items-center gap-4">
        <div className="relative h-32 w-32 shrink-0">
          <svg viewBox="0 0 120 120" className="h-32 w-32 -rotate-90">
            <circle cx="60" cy="60" r={R} fill="none" stroke="hsl(var(--muted))" strokeWidth="10" />
            <circle
              cx="60"
              cy="60"
              r={R}
              fill="none"
              stroke="hsl(var(--primary))"
              strokeWidth="10"
              strokeLinecap="round"
              strokeDasharray={C}
              strokeDashoffset={loading ? C : offset}
              style={{ transition: "stroke-dashoffset 900ms ease" }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="font-display text-2xl font-bold text-foreground tabular-nums">
              {progressPct}%
            </span>
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">of year</span>
          </div>
        </div>
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
            Your journey
          </p>
          <p className="font-display text-lg font-semibold text-foreground mt-1">
            Day {daysIn} of 365
          </p>
          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5">
            <Calendar className="h-3 w-3" /> {startedLabel} → {expiresLabel}
          </p>
          <p className="text-xs text-primary font-medium mt-1.5">{daysLeft} days remaining</p>
        </div>
      </CardContent>
    </Card>
  );
}

function StatTile({
  icon: Icon,
  label,
  value,
  total,
  suffix,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number;
  total?: number;
  suffix?: string;
}) {
  return (
    <div className="rounded-2xl border border-border bg-card/40 p-4">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <p className="font-display text-2xl font-bold text-foreground mt-1 tabular-nums">
        {value}
        {suffix ? <span className="text-base text-muted-foreground ml-1">{suffix}</span> : null}
        {total !== undefined ? (
          <span className="text-base text-muted-foreground ml-1">/ {total}</span>
        ) : null}
      </p>
    </div>
  );
}

function ReadinessTile({ readiness }: { readiness: ReadinessBreakdown | null }) {
  const value = readiness?.score ?? 0;
  return (
    <TooltipProvider delayDuration={150}>
      <UiTooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="rounded-2xl border border-border bg-card/40 p-4 text-left hover:border-primary/40 transition focus:outline-none focus:ring-2 focus:ring-primary/40"
            aria-label="Future readiness — how this is calculated"
          >
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
              <TrendingUp className="h-3.5 w-3.5" /> Future readiness
              <Info className="h-3 w-3 ml-auto text-muted-foreground/70" />
            </div>
            <p className="font-display text-2xl font-bold text-foreground mt-1 tabular-nums">
              {readiness ? value : "—"}
              <span className="text-base text-muted-foreground ml-1">%</span>
            </p>
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" align="end" className="max-w-xs p-3 space-y-2">
          <p className="text-xs font-semibold text-foreground">
            How your score is calculated
          </p>
          <ul className="space-y-1.5">
            {(readiness?.parts ?? []).map((p) => (
              <li key={p.label} className="text-[11px] flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium text-foreground">{p.label}</p>
                  <p className="text-muted-foreground leading-snug">{p.detail}</p>
                </div>
                <span className="tabular-nums text-foreground shrink-0">
                  +{p.points}<span className="text-muted-foreground">/{p.max}</span>
                </span>
              </li>
            ))}
          </ul>
          <p className="text-[10px] text-muted-foreground pt-1 border-t border-border">
            Capped at 100. Updates as you complete reflections, badges, and refreshes.
          </p>
        </TooltipContent>
      </UiTooltip>
    </TooltipProvider>
  );
}


function TimelineRow({ step, index }: { step: ScheduledStep; index: number }) {
  const Icon = KIND_ICON[step.kind];
  const s = STATUS_STYLES[step.status];
  const StatusIcon =
    step.status === "completed" ? CheckCircle2 : step.status === "locked" ? Lock : Circle;
  return (
    <motion.li
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.35, delay: Math.min(index * 0.04, 0.4) }}
      className="ml-6 pb-6"
    >
      <span
        className={`absolute -left-[13px] flex h-6 w-6 items-center justify-center rounded-full ring-4 ring-background ${s.dot}`}
      >
        <Icon className="h-3 w-3" />
      </span>
      <div
        className={`rounded-2xl border bg-card/60 backdrop-blur p-4 sm:p-5 ring-1 ${s.ring} hover:bg-card transition`}
      >
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
              Month {step.monthIndex} · {formatMonth(step.scheduledFor)}
            </p>
            <h3 className="font-display text-lg font-semibold text-foreground mt-0.5">
              {step.title}
            </h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-xl">{step.blurb}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span
              className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium ${s.pill}`}
            >
              <StatusIcon className="h-3 w-3" /> {s.pillLabel}
            </span>
            {step.kind === "reflection" && step.status !== "locked" && step.status !== "upcoming" && (
              <Button asChild size="sm" variant={step.status === "completed" ? "ghost" : "default"}>
                <Link
                  to={`/reflect/${step.monthIndex}`}
                  onClick={() => analytics.journeyMilestoneCtaClick({ kind: step.kind, month_index: step.monthIndex, status: step.status, action: step.status === "completed" ? "edit" : "reflect" })}
                >
                  {step.status === "completed" ? "Edit" : "Reflect"}
                </Link>
              </Button>
            )}
          </div>
        </div>
      </div>
    </motion.li>
  );
}
